import { useNavigate } from 'react-router-dom';
import type { CommunicationsSummary } from '../../../services/communicationsSummary';
import { HubStatCard } from './HubStatCard';

const STAT_PLACEHOLDER = '-';

interface HubStatsRowProps {
    practicesTotal: number;
    practicesAddedThisMonth: number;
    summary: CommunicationsSummary | null;
    /** True while the section-wide practices count (see AccountsOverview's usePractices call) failed to load. */
    practicesError?: boolean;
    /** True while useCommunicationsSummary failed to load. */
    summaryError?: boolean;
    /** Retries whichever of the two queries above failed. Only rendered when at least one did. */
    onRetry?: () => void;
}

// Four cards - "Open action items" and "Overdue items" are dropped outright
// (the Action Items feature is cancelled), leaving grid-cols-4.
//
// A failed practicesTotal/summary fetch shows a muted "-" placeholder in the
// affected cells (never a false zero) plus one shared inline error + Retry
// strip below the row, rather than a Retry button per cell.
export function HubStatsRow({
    practicesTotal,
    practicesAddedThisMonth,
    summary,
    practicesError = false,
    summaryError = false,
    onRetry
}: HubStatsRowProps) {
    const navigate = useNavigate();
    const week = summary?.week ?? { total: 0, incoming: 0, outgoing: 0 };
    const unresolved = summary?.unresolved ?? 0;
    const unassigned = summary?.unassigned ?? 0;
    const hasError = practicesError || summaryError;

    return (
        <div className="flex flex-col gap-2">
            <div className="grid grid-cols-4 gap-3">
                <HubStatCard
                    label="Practices"
                    value={practicesError ? STAT_PLACEHOLDER : practicesTotal}
                    sub={practicesError ? undefined : `${practicesAddedThisMonth} added this month`}
                    tone={practicesError ? 'muted' : undefined}
                    onClick={() => navigate('/accounts/practices')}
                />
                <HubStatCard
                    label="Communications this week"
                    value={summaryError ? STAT_PLACEHOLDER : week.total}
                    sub={
                        summaryError
                            ? undefined
                            : `${week.incoming} incoming · ${week.outgoing} outgoing`
                    }
                    tone={summaryError ? 'muted' : undefined}
                />
                <HubStatCard
                    label="Unresolved threads"
                    value={summaryError ? STAT_PLACEHOLDER : unresolved}
                    tone={summaryError ? 'muted' : unresolved > 0 ? 'yellow' : 'gray'}
                />
                <HubStatCard
                    label="Threads with no practice"
                    value={summaryError ? STAT_PLACEHOLDER : unassigned}
                    sub={summaryError ? undefined : 'need a practice attached'}
                    tone={summaryError ? 'muted' : undefined}
                />
            </div>
            {hasError && onRetry && (
                <div className="flex items-center gap-2 px-1">
                    <span className="text-xs text-red-500 font-medium">
                        Couldn&apos;t load some stats.
                    </span>
                    <button
                        type="button"
                        onClick={onRetry}
                        className="text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg px-2.5 py-1 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            )}
        </div>
    );
}
